import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import './CardSurface.css';

export type CardSurfaceBackground = 'white' | 'muted';
export type CardSurfacePadding = 'none' | 'small' | 'medium' | 'large';

/** Bare card container with Spark chrome (rounded corners, optional border) but no header/footer. */
export type CardSurfaceProps = {
  /** Surface fill token: `white` (#FFFFFF) or `muted` (#FCFCFD). */
  background?: CardSurfaceBackground;
  /** Adds a 1px `--semantic-border-default` border. */
  bordered?: boolean;
  /** Inner padding scale mapped to Spark spacing tokens. */
  padding?: CardSurfacePadding;
  children?: ReactNode;
  className?: string;
} & Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'className'>;

/**
 * CardSurface — a headerless, footerless Spark card.
 *
 * A bare container that carries the Spark card chrome (12px radius, optional
 * `--semantic-border-default` border) but none of `CardDefault`'s header,
 * footer, min-height, or max-width constraints. Use it when a design calls for
 * a "card look" around arbitrary content (e.g. an editable text surface).
 */
export const CardSurface = forwardRef<HTMLDivElement, CardSurfaceProps>(function CardSurface(
  { background = 'white', bordered = true, padding = 'medium', children, className = '', ...rest },
  ref,
) {
  const classes = [
    'spark-card-surface',
    `spark-card-surface-bg-${background}`,
    bordered ? 'spark-card-surface-bordered' : '',
    `spark-card-surface-pad-${padding}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div ref={ref} className={classes} {...rest}>
      {children}
    </div>
  );
});
